import * as React from 'react';
import {Helmet} from 'react-helmet';

import {SubmissionError} from 'redux-form';

import {getStandardTimezoneOffset} from 'nutshell-core/date-time/get-standard-timezone-offset';
import {Routing} from 'nutshell-core/routing';
import {makeErrorObject} from 'nutshell-core/validation/make-error-object';
import {
    validationErrorCheck,
    ValidationError,
} from 'nutshell-core/validation/validation-error-check';

import {
    authFormEncodedApi,
    getAuthUrl,
    getMicrosoftOauthUrl,
    trackGoogleLoginError,
    isMobileAuth,
    buildLoginData,
    getMobileUdid,
    loginWithAuthToken,
} from '../utils';

import {LoginForm} from './login-form';

export type LoginVariant = 'sign-in' | 'password' | 'choose-method';

export function LoginPage() {
    const [googleErrorMessage, setGoogleErrorMessage] = React.useState<any>(null);
    const [microsoftErrorMessage, setMicrosoftErrorMessage] = React.useState<any>(null);
    const [ssoErrorMessage, setSsoErrorMessage] = React.useState<any>(null);
    const [variant, setVariant] = React.useState<LoginVariant>('sign-in');
    const [ssoRedirectUrl, setSsoRedirectUrl] = React.useState<string | null>(null);
    const urlParamString = window.location.search.slice(1); // Remove "?"
    const urlParams = Routing.deparam(urlParamString);

    // To capture error messages from SSO, we take it from the URL as there is
    // no waiting request after user is redirected from SSO with an error.
    React.useEffect(() => {
        if (urlParams.sso_error || urlParams.error_description) {
            setSsoErrorMessage('Invalid SSO login attempt. Please try again or contact support.');
            window.history.replaceState({}, '', getAuthUrl('/auth'));
        }
    }, [urlParams.sso_error, urlParams.error_description]);

    const microsoftOauthUrl = getMicrosoftOauthUrl();

    // @ts-expect-error
    function handleContinue(formData) {
        return new Promise((resolve, reject) => {
            authFormEncodedApi
                .post('/auth/on-continue', formData, {redirect: 'follow'})
                // @ts-expect-error
                .then((res) => res.json())
                .then((data) => {
                    if (data && data.success) {
                        if (data.redirectUrl && data.canUsePasswordAndSso) {
                            setVariant('choose-method');
                            setSsoRedirectUrl(getAuthUrl(data.redirectUrl));
                            // @ts-expect-error
                            resolve();
                        } else if (data.redirectUrl && !data.canUsePasswordAndSso) {
                            window.location.href = getAuthUrl(data.redirectUrl);
                            // @ts-expect-error
                            resolve();
                        } else {
                            setVariant('password');
                            // @ts-expect-error
                            resolve();
                        }
                    }
                })
                .catch((err) => {
                    if (err.httpResponse.status === 400) {
                        reject(
                            new SubmissionError({
                                _error: 'We couldn’t find a Nutshell account for that email address.',
                            })
                        );
                    } else {
                        reject(
                            new SubmissionError({
                                _error: 'Error continuing with login, please try again or contact support.',
                            })
                        );
                    }
                });
        });
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>Nutshell | Log in to Nutshell</title>
            </Helmet>
            <LoginForm
                initialValues={{
                    via: 'database',
                    timezone_offset: (getStandardTimezoneOffset() / 60) * -1,
                    remember_me: true,
                    username: urlParams.email,
                    // Encompasses both CSRF and expired password reset token,
                    // we show the same generic error message
                    invalidToken: Boolean(urlParams.invalidCsrf) || Boolean(urlParams.invalidToken),
                    sessionTimeout: Boolean(urlParams.sessionTimeout),
                }}
                // @ts-expect-error
                onContinue={handleContinue}
                ssoErrorMessage={ssoErrorMessage}
                onEmailLogin={handleEmailLogin}
                variant={variant}
                ssoRedirectUrl={ssoRedirectUrl}
                setVariant={setVariant}
                googleErrorMessage={googleErrorMessage}
                microsoftErrorMessage={microsoftErrorMessage}
                onGoogleIdentityFailure={() => {
                    trackGoogleLoginError();

                    // @ts-expect-error
                    this.setState({
                        googleErrorMessage: 'Unknown error connecting to Google. Please try again.',
                    });
                }}
                onGoogleSubmit={(token: string) => {
                    return handleGoogleLogin(token).catch((err: Error) => {
                        const errorMessage = getErrorMessage(err);
                        setGoogleErrorMessage(errorMessage);
                    });
                }}
                // @ts-expect-error
                onMicrosoftIdentityFailure={(error) => {
                    setMicrosoftErrorMessage(
                        error && error.message
                            ? error.message
                            : 'Unknown error connecting to Microsoft. Please try again.'
                    );
                }}
                // @ts-expect-error
                onMicrosoftSubmit={(oauthResponse) => {
                    return new Promise((resolve) => {
                        return handleMicrosoftLogin(oauthResponse).catch((error: Error) => {
                            const errorMessage = getErrorMessage(error);
                            setMicrosoftErrorMessage(errorMessage);
                            // @ts-expect-error
                            resolve();
                        });
                    });
                }}
                microsoftOauthUrl={microsoftOauthUrl}
                showSignup={false}
                isMobile={isMobileAuth()}
            />
        </React.Fragment>
    );
}

function getErrorMessage(error: Error): string {
    // @ts-expect-error
    if (error instanceof ValidationError && error.validationResult) {
        const errorObject = makeErrorObject(error);
        if (errorObject && errorObject._error && Array.isArray(errorObject._error)) {
            return errorObject._error[0];
        } else {
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track('unknown google auth validation error');
                // @ts-expect-error
                window.trackJs.track(errorObject);
            }

            return 'Not able to log into Nutshell using SSO at this time.';
        }
    } else {
        // @ts-expect-error
        if (typeof window.trackJs !== 'undefined') {
            // @ts-expect-error
            window.trackJs.track('unknown google auth error');
            // @ts-expect-error
            window.trackJs.track(error);
        }

        return 'Not able to log into Nutshell using SSO at this time.';
    }
}

function handleMicrosoftLogin(token: string): Promise<any> {
    return attemptNutshellLogin({
        password: token,
        strategy: 'o365',
        via: 'database',
        timezone_offset: (getStandardTimezoneOffset() / 60) * -1,
        remember_me: true,
    });
}

function handleGoogleLogin(token: string): Promise<any> {
    return attemptNutshellLogin({
        password: token,
        strategy: 'google',
        via: 'database',
        timezone_offset: (getStandardTimezoneOffset() / 60) * -1,
        remember_me: true,
    });
}

// @ts-expect-error
function attemptLogin(formData, resolve, reject) {
    attemptNutshellLogin(formData)
        .then((res) => {
            resolve(res);
        })
        .catch((err) => {
            // @ts-expect-error
            if (err instanceof ValidationError && err.validationResult) {
                reject(new SubmissionError(makeErrorObject(err)));
            }

            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(`Could not log in: ${JSON.stringify(err, null, 2)}`);
            }

            reject(
                new SubmissionError({
                    _error: 'There was an error logging in.  Please refresh the page and try again, or contact support.',
                })
            );
        });
}

// @ts-expect-error
function handleEmailLogin(formData): Promise<any> {
    if (formData.mfaCode) {
        return new Promise((resolve, reject) => {
            return (
                authFormEncodedApi
                    .post('/auth/mfa', {code: formData.mfaCode}, {redirect: 'follow'})
                    .then((res) => {
                        // @ts-expect-error
                        if (res && res.redirected) {
                            // @ts-expect-error
                            window.location.href = res.url;
                        }

                        return res;
                    })
                    // @ts-expect-error
                    .then(validationErrorCheck)
                    // @ts-expect-error
                    .then((res) => res.json())
                    .catch((err) => {
                        // @ts-expect-error
                        if (err instanceof ValidationError && err.validationResult) {
                            reject(new SubmissionError(makeErrorObject(err)));
                        }

                        reject(
                            new SubmissionError({
                                _error: 'There was an error logging in.  Please refresh the page and try again, or contact support.',
                            })
                        );
                    })
            );
        });
    }

    return new Promise((resolve, reject) => {
        if (shouldBypassMobileRecaptcha(formData.username)) {
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.configure({userId: formData.username});
                // @ts-expect-error
                window.trackJs.track(
                    'LoginPage: Mobile auth skipping recaptcha (allowlisted user).'
                );
            }

            attemptLogin({...formData, recaptchaToken: 'SCRIPT_BLOCKED'}, resolve, reject);
            return;
        }

        // @ts-expect-error
        if (typeof window.grecaptcha === 'undefined') {
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(`LoginPage: Recaptcha code never loaded.`);
            }

            // No recaptcha included
            attemptLogin({...formData, recaptchaToken: 'SCRIPT_BLOCKED'}, resolve, reject);
        } else {
            // @ts-expect-error
            grecaptcha.ready(() => {
                grecaptcha
                    // @ts-expect-error
                    .execute(window.RecaptchaConfig.publicApiKey, {action: 'login'})
                    // @ts-expect-error
                    .then((token) => {
                        attemptLogin(
                            {
                                ...formData,
                                recaptchaToken: token,
                            },
                            resolve,
                            reject
                        );
                    })
                    .catch(() => {
                        // @ts-expect-error
                        if (typeof window.trackJs !== 'undefined') {
                            // @ts-expect-error
                            window.trackJs.track(
                                `LoginPage: Recaptcha code could not produce recaptcha token.`
                            );
                        }

                        attemptLogin(
                            {...formData, recaptchaToken: 'COULD_NOT_PRODUCE_TOKEN'},
                            resolve,
                            reject
                        );
                    });
            });
        }
    });
}

// ESC-305: temporary bypass for a single mobile user while we confirm recaptcha ready() hang in WebView.
const MOBILE_RECAPTCHA_BYPASS_USERNAMES = ['flynn@ultimate-solutions.co.nz'];

function shouldBypassMobileRecaptcha(username?: string | null): boolean {
    if (!isMobileAuth() || !username) {
        return false;
    }

    return MOBILE_RECAPTCHA_BYPASS_USERNAMES.includes(username.toLowerCase());
}

type FormData = {
    username?: string;
    password: string;
    remember_me: boolean;
    strategy: string;
    timezone_offset: number;
    via: string;
};

function attemptNutshellLogin(formData: FormData): Promise<any> {
    // We need to remove the `X-Requested-With` header, otherwise the backend
    // treats this as a json request, and won't redirect.
    if (
        authFormEncodedApi.options.headers &&
        // @ts-expect-error
        typeof authFormEncodedApi.options.headers.delete === 'function'
    ) {
        // @ts-expect-error
        authFormEncodedApi.options.headers.delete('X-Requested-With');
    }

    const isMobile = isMobileAuth();
    const udid = isMobile ? getMobileUdid() : null;

    const loginData = buildLoginData(formData, isMobile, udid);

    if (isMobile) {
        return (
            authFormEncodedApi
                .post('/auth', loginData)
                // @ts-expect-error
                .then(validationErrorCheck)
                // @ts-expect-error
                .then((response) => response.json())
                .then((data) => {
                    if (data && data.authToken) {
                        loginWithAuthToken(formData.username, data.authToken);
                    }
                })
        );
    }

    // At least for now, this is to detect if the user is in the oauth flow
    const callback = new URLSearchParams(window.location.search).get('callback');

    return (
        authFormEncodedApi
            .post('/auth', {...formData, callback}, {redirect: 'follow'})
            .then((res) => {
                // @ts-expect-error
                if (res && res.redirected) {
                    // @ts-expect-error
                    window.location.href = res.url;
                }

                return res;
            })
            // @ts-expect-error
            .then(validationErrorCheck)
            // @ts-expect-error
            .then((res) => res.json())
    );
}
